import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, LayoutGrid, Map as MapIcon } from "lucide-react";
import { CATEGORIES, avgScore } from "../mock";
import { usePlaces } from "../context/PlacesContext";
import { PlaceCard, PlaceRow } from "../components/Bits";

export default function Explore() {
  const [params] = useSearchParams();
  const { places, geo, loading } = usePlaces();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [country, setCountry] = useState(params.get("country") || "All");
  const [region, setRegion] = useState(params.get("region") || "All");
  const [city, setCity] = useState(params.get("city") || "All");
  const [sort, setSort] = useState("score");
  const [view, setView] = useState("grid");

  useEffect(() => {
    setCountry(params.get("country") || "All");
    setRegion(params.get("region") || "All");
    setCity(params.get("city") || "All");
  }, [params]);

  const regionOptions = useMemo(() => {
    if (country === "All") {
      return [...new Set(Object.values(geo.regionsByCountry).flat())];
    }
    return geo.regionsByCountry[country] || [];
  }, [country, geo]);

  const cityOptions = useMemo(() => {
    if (country === "All") return [];
    if (region === "All") return geo.citiesByCountry[country] || [];
    return geo.citiesByRegion[`${country}|${region}`] || [];
  }, [country, region, geo]);

  const setCountryAndReset = (c) => { setCountry(c); setRegion("All"); setCity("All"); };
  const setRegionAndReset = (r) => { setRegion(r); setCity("All"); };

  const results = useMemo(() => {
    let list = places.filter((p) => {
      const matchQ = !q ||
        p.name.toLowerCase().includes(q.toLowerCase()) ||
        p.cuisine.toLowerCase().includes(q.toLowerCase()) ||
        p.city.toLowerCase().includes(q.toLowerCase()) ||
        p.neighborhood.toLowerCase().includes(q.toLowerCase());
      const matchCat = cat === "All" || p.category === cat;
      const matchCountry = country === "All" || p.country === country;
      const matchRegion = region === "All" || p.region === region;
      const matchCity = city === "All" || p.city === city;
      return matchQ && matchCat && matchCountry && matchRegion && matchCity;
    });
    return [...list].sort((a, b) =>
      sort === "score" ? avgScore(b.scores) - avgScore(a.scores) : a.name.localeCompare(b.name)
    );
  }, [places, q, cat, country, region, city, sort]);

  const mapQuery =
    city !== "All" ? `${city}, ${region !== "All" ? region + ", " : ""}${country !== "All" ? country : ""}`
    : region !== "All" ? `${region}, ${country}`
    : country !== "All" ? country
    : "world";

  const selectBase = "bg-paper border border-ink/15 px-4 py-2 text-sm outline-none focus:border-claret cursor-pointer";

  return (
    <div className="pt-[68px]">
      <div className="bg-ink text-paper relative grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 relative">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-brass" />
            <span className="eyebrow text-brass">Explore</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-semibold display-balance max-w-2xl">
            Find your next place worth experiencing.
          </h1>
          <p className="text-paper/60 mt-5 max-w-xl text-lg">
            Search and filter every reviewed restaurant, bar, café and experience — across the globe.
          </p>
          <div className="mt-10 flex items-center gap-3 bg-paper max-w-2xl px-5 py-4">
            <Search className="w-5 h-5 text-ink-soft" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search places, cuisines, cities…" className="flex-1 bg-transparent text-ink placeholder-ink-soft/60 outline-none" />
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap items-center gap-6 justify-between pb-8 border-b border-ink/10">
          <div className="flex flex-wrap items-center gap-2">
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => setCat(c)} className={`px-4 py-2 text-[12px] font-semibold tracking-wide transition-colors ${cat === c ? "bg-claret text-paper" : "bg-ink/5 text-ink-soft hover:bg-ink/10"}`}>
                {c}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select value={country} onChange={(e) => setCountryAndReset(e.target.value)} className={selectBase}>
              <option value="All">All countries</option>
              {geo.countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={region} onChange={(e) => setRegionAndReset(e.target.value)} className={selectBase} disabled={country === "All"}>
              <option value="All">{country === "All" ? "All regions" : "All of " + country}</option>
              {regionOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={city} onChange={(e) => setCity(e.target.value)} className={selectBase} disabled={!cityOptions.length}>
              <option value="All">All cities</option>
              {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className={selectBase}>
              <option value="score">Highest rated</option>
              <option value="name">A – Z</option>
            </select>
            <div className="flex border border-ink/15">
              <button onClick={() => setView("grid")} className={`p-2.5 ${view === "grid" ? "bg-ink text-paper" : "text-ink-soft"}`} aria-label="Grid view"><LayoutGrid className="w-4 h-4" /></button>
              <button onClick={() => setView("map")} className={`p-2.5 ${view === "map" ? "bg-ink text-paper" : "text-ink-soft"}`} aria-label="Map view"><MapIcon className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        <p className="text-sm text-ink-soft mt-6 mb-8">
          {loading ? "Loading…" : `${results.length} ${results.length === 1 ? "place" : "places"}`}
          {!loading && (city !== "All" ? ` in ${city}` : region !== "All" ? ` in ${region}` : country !== "All" ? ` in ${country}` : " worldwide")}
        </p>

        {view === "grid" ? (
          results.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14">
              {results.map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
            </div>
          ) : !loading ? (
            <div className="py-24 text-center text-ink-soft">
              <p className="font-serif text-2xl">No places match your search yet.</p>
              <p className="mt-2 text-sm">Try clearing a filter — our catalogue is growing weekly.</p>
            </div>
          ) : null
        ) : (
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8">
            <div className="max-h-[640px] overflow-y-auto pr-2">
              {results.length ? results.map((p) => <PlaceRow key={p.id} place={p} />) : <p className="text-ink-soft py-10">No places match your filters.</p>}
            </div>
            <div className="relative bg-ink/5 h-[640px] sticky top-24 overflow-hidden">
              <iframe title="Placeworthy map" src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=${city !== "All" ? 12 : region !== "All" ? 6 : 3}&output=embed`} className="w-full h-full grayscale-[0.2]" loading="lazy" style={{ border: 0 }} />
              <div className="absolute top-4 left-4 bg-paper px-4 py-2 shadow-lg">
                <span className="eyebrow text-claret text-[10px]">Now exploring</span>
                <p className="font-serif text-lg font-semibold">{mapQuery === "world" ? "The whole world" : mapQuery}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
